import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Plus, Minus } from 'lucide-react-native';
import { Icon } from '@/components/ui/icon';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { RandomUtils } from '@fytness/shared';
import { logger } from '@/lib/logger';
import { WorkoutSetValue } from './WorkoutSetValue';
import type {
  WorkoutSetRecord,
  WorkoutExerciseDimensionRecord,
  WorkoutSetValueRecord,
} from '~/powersync/generated/schema';

interface SetsDimensionTableProps {
  /**
   * The exercise ID to display sets for
   */
  exerciseId: string;
  /**
   * The workout ID for creating sets
   */
  workoutId: string;
}

export const SetsDimensionTable: React.FC<SetsDimensionTableProps> = ({
  exerciseId,
  workoutId,
}) => {
  const { t } = useTranslation('workout');
  const powerSync = usePowerSync();

  // Fetch workout sets for this exercise using clean local-api
  const {
    data: workoutSets,
    isLoading: isLoadingSets,
    error: setsError,
  } = useLivePowerSyncQuery<WorkoutSetRecord>(
    db.Workout.queries.findSetsByExercise(exerciseId),
    [exerciseId],
  );

  // Fetch workout exercise dimensions using clean local-api
  const {
    data: dimensions,
    isLoading: isLoadingDimensions,
    error: dimensionsError,
  } = useLivePowerSyncQuery<WorkoutExerciseDimensionRecord>(
    db.Workout.queries.findDimensionsByExercise(exerciseId),
    [exerciseId],
  );

  // Clean local-api mutation for adding a new workout set
  const addSetMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.createSet(powerSync, exerciseId, workoutId),
  });

  // Clean local-api mutation for removing the latest workout set
  const removeSetMutation = useMutation({
    mutationFn: () => {
      if (!workoutSets || workoutSets.length === 0) {
        return Promise.resolve();
      }

      // Get the latest set (last in the ordered array)
      const latestSet = workoutSets[workoutSets.length - 1];

      return db.Workout.mutations.deleteSet(powerSync, latestSet.id);
    },
  });

  const isLoading = isLoadingSets || isLoadingDimensions;
  const hasError = Boolean(setsError || dimensionsError);

  if (isLoading) {
    return <Text className="text-sm text-text-muted">{t('sets.loading')}</Text>;
  }

  if (hasError) {
    logger.error('Failed to load sets data');
    return <Text className="text-sm text-destructive">{t('sets.error')}</Text>;
  }

  if (!workoutSets || workoutSets.length === 0) {
    return (
      <View>
        <Text className="text-sm text-text-muted mb-3">{t('sets.noSets')}</Text>

        {/* Add Set Button */}
        <Button
          testID="set-add-button"
          variant="outline"
          size="sm"
          onPress={() => addSetMutation.mutate()}
          disabled={addSetMutation.isPending}
          className="flex-row items-center gap-2 self-start"
        >
          <Icon as={Plus} size={14} className="text-text-primary" />
          <Text className="text-sm">Add Set</Text>
        </Button>
      </View>
    );
  }

  return (
    <View>
      {/* Header */}
      <View className="flex-row mb-2">
        <View className="w-[10%] lg:w-[5%]">
          <Text className="text-xs font-medium text-text-secondary">#</Text>
        </View>
        {dimensions?.map((dimension) => {
          const showUnit =
            dimension.unit && dimension.unit.toUpperCase() !== 'NONE';
          return (
            <View
              key={dimension.id}
              style={{ flex: 1 }}
              className="items-center"
            >
              <Text className="text-xs font-medium text-text-secondary text-center">
                {t(`dimension.${dimension.dimension}.name`)}
                {showUnit && ` (${t(`unit.${dimension.unit}.shortName`)})`}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Rows */}
      <View className="space-y-1">
        {workoutSets.map((set, index) => (
          <View key={set.id} className="flex-row py-1 items-center">
            <View className="w-[10%] lg:w-[5%]">
              <Text className="text-sm">{index + 1}</Text>
            </View>
            {dimensions?.map((dimension) => (
              <View key={dimension.id} style={{ flex: 1 }} className="px-1">
                <WorkoutSetValue.Synced
                  setId={set.id}
                  exerciseDimensionId={dimension.id}
                  workoutId={workoutId}
                />
              </View>
            ))}
          </View>
        ))}
      </View>

      {/* Add/Remove Set Buttons */}
      <View className="flex-row gap-2 mt-3 justify-end">
        <Button
          testID="set-add-button"
          variant="subtle"
          size="icon"
          onPress={() => addSetMutation.mutate()}
          disabled={addSetMutation.isPending}
          className="h-8 w-8 rounded-md"
        >
          <Icon as={Plus} size={14} className="text-text-secondary" />
        </Button>

        <Button
          variant="subtle"
          size="icon"
          onPress={() => removeSetMutation.mutate()}
          disabled={removeSetMutation.isPending || workoutSets.length === 0}
          className="h-8 w-8 rounded-md"
        >
          <Icon as={Minus} size={14} className="text-text-secondary" />
        </Button>
      </View>
    </View>
  );
};
